// ─── GUATEMALA TIMEZONE (UTC-6) ─────────────────────────────────────────────
export const GT_TZ = 'America/Guatemala';
export const toGT = (date) => {
  if (!date) return new Date();
  const s = String(date);
  const utc = s.endsWith('Z') || s.includes('+') ? s : s.replace(' ','T') + 'Z';
  return new Date(utc);
};
export const fmtGTDate = (date) => toGT(date).toLocaleDateString('es-GT', { timeZone: GT_TZ, day:'2-digit', month:'2-digit', year:'numeric' });
export const fmtGTDateTime = (date) => toGT(date).toLocaleString('es-GT', { timeZone: GT_TZ, day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit', second:'2-digit', hour12:true });
export const fmtGTTime = (date) => toGT(date).toLocaleTimeString('es-GT', { timeZone: GT_TZ, hour:'2-digit', minute:'2-digit', second:'2-digit', hour12:true });

// Fecha (YYYY-MM-DD) en Guatemala. `records.created_at` es timestamp SIN zona
// guardado en UTC: compararlo contra `new Date().toISOString()` hacía que de las
// 6 pm en adelante "hoy" saltara al día siguiente y los cuadros del día se
// vaciaran en plena jornada.
export const fechaGT = (ts) => (ts ? toGT(ts).toLocaleDateString('en-CA', { timeZone: GT_TZ }) : '');
export const hoyGT   = () => new Date().toLocaleDateString('en-CA', { timeZone: GT_TZ });

// Lo que vale una orden, con el MISMO criterio que la base (`public.monto_orden`,
// la que usa el dashboard de gerencia): el primer total final no-cero de las
// variantes históricas de la columna y, si no hay ninguna, el subtotal menos el
// descuento. `records.total` es el subtotal de lista, nunca lo cobrado: sumarlo
// a secas infla el ingreso en toda orden con convenio o descuento.
export const montoOrden = (r) => {
  if (!r) return 0;
  const n = (v) => Number(v) || 0;
  // `total` YA es el neto cobrado (convenio y descuento de caja incluidos);
  // `descuento` es solo informativo. Restarlo otra vez duplicaba el convenio
  // (bug 18→24-sep-2026). Mismo criterio que `public.monto_orden`.
  return n(r.total_final) || n(r.totalfinal) || n(r.totalFinal) || n(r.total);
};

// ─── ROLES ───────────────────────────────────────────────────────────────────
export const ROLES = {
  admin:  { label: 'Administrador',   color: '#152643' },
  qb:     { label: 'Químico Biólogo', color: '#4A8CF0' },
  op:     { label: 'Operador',        color: '#059669' },
  medico: { label: 'Médico',          color: '#7c3aed' },
  secretaria: { label: 'Secretaria',  color: '#0d9488' },
  manager:    { label: 'Manager',     color: '#152643' },
  embriologa: { label: 'Embrióloga',  color: '#5a3fb0' },
  socio:      { label: 'Socio',       color: '#152643' },
};

// ─── THEME (modo claro) ───────────────────────────────────────────────────────
export const C = {
  bg:'#F4F6FA', navy:'#152643', navyL:'#0F1D36', teal:'#4A8CF0', tealL:'#DCE9FF',
  border:'#dde3ea', borderDk:'#b8c4d0', text:'#1e293b', muted:'#64748b', mutedL:'#94a3b8',
  white:'#ffffff', green:'#059669', greenL:'#d1fae5', red:'#dc2626', redL:'#fee2e2',
  amber:'#d97706', amberL:'#fef3c7', accent:'#63A0FF',
};

// ─── TEMA OSCURO ─────────────────────────────────────────────────────────────
export const DARK = {
  bg:'#0b1929', navy:'#071422', navyL:'#0d1f33', card:'#112236', cardHover:'#162d47',
  border:'rgba(0,180,216,0.13)', borderAcc:'rgba(0,180,216,0.28)',
  teal:'#4A8CF0', tealL:'rgba(0,180,216,0.12)',
  text:'#e2f0f7', muted:'#7aafc8', hint:'#4a7a96',
  white:'#e8f4f8', green:'#22c55e', greenL:'rgba(34,197,94,0.14)',
  red:'#ef4444', redL:'rgba(239,68,68,0.14)',
  amber:'#f59e0b', amberL:'rgba(245,158,11,0.14)',
};

// ─── ESTILOS BASE (modo claro) ────────────────────────────────────────────────
export const g = {
  app:{fontFamily:"'Inter','Segoe UI',system-ui,sans-serif",background:C.bg,minHeight:'100vh',color:C.text},
  topBar:{background:C.navy,padding:'0 20px',display:'flex',alignItems:'center',gap:14,height:60,boxShadow:'0 2px 12px rgba(0,0,0,.35)',flexWrap:'wrap'},
  tabs:{background:C.navyL,display:'flex',borderBottom:`1px solid ${C.border}`,overflowX:'auto'},
  tab:(a)=>({padding:'13px 18px',cursor:'pointer',fontSize:13,fontFamily:"'Inter','Segoe UI',sans-serif",fontWeight:a?'700':'500',letterSpacing:0.3,color:a?C.white:C.mutedL,background:a?C.teal:'transparent',borderBottom:a?`3px solid ${C.accent}`:'3px solid transparent',whiteSpace:'nowrap',border:'none',transition:'all .15s'}),
  corrBar:{background:`${C.navyL}f5`,padding:'10px 20px',display:'flex',alignItems:'center',gap:16,borderBottom:`2px solid ${C.teal}`,flexWrap:'wrap'},
  main:{padding:'24px 20px',maxWidth:1000,margin:'0 auto'},
  card:{background:C.white,border:`1px solid ${C.border}`,borderRadius:12,padding:22,marginBottom:16,boxShadow:'0 2px 8px rgba(0,0,0,.07)'},
  secT:{fontSize:13,fontWeight:'700',letterSpacing:0.5,color:C.navy,marginBottom:14,paddingBottom:8,borderBottom:`2px solid ${C.teal}30`,display:'flex',alignItems:'center',gap:8},
  g2:{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14},
  g3:{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:14},
  lbl:{fontSize:11,fontWeight:'600',color:C.muted,letterSpacing:0.5,marginBottom:5,display:'block',textTransform:'uppercase'},
  inp:{width:'100%',padding:'10px 13px',border:`1.5px solid ${C.borderDk}`,borderRadius:8,fontSize:14,color:C.text,fontFamily:'inherit',outline:'none',background:C.white,boxSizing:'border-box',transition:'border-color .15s'},
  sel:{width:'100%',padding:'10px 13px',border:`1.5px solid ${C.borderDk}`,borderRadius:8,fontSize:14,color:C.text,fontFamily:'inherit',outline:'none',background:C.white,boxSizing:'border-box'},
  ta:{width:'100%',padding:'10px 13px',border:`1.5px solid ${C.borderDk}`,borderRadius:8,fontSize:14,color:C.text,fontFamily:'inherit',outline:'none',background:C.white,boxSizing:'border-box',resize:'vertical',minHeight:84},
  btn:(c=C.teal)=>({padding:'9px 18px',borderRadius:8,border:`1.5px solid ${c}`,background:`${c}12`,color:c,cursor:'pointer',fontSize:13,fontFamily:'inherit',fontWeight:'600',letterSpacing:0.3,transition:'all .15s'}),
  btnS:(c=C.teal)=>({padding:'10px 22px',borderRadius:8,border:'none',background:c,color:C.white,cursor:'pointer',fontSize:13,fontFamily:'inherit',fontWeight:'700',letterSpacing:0.3,transition:'all .15s'}),
  chk:(on)=>({width:17,height:17,borderRadius:4,border:`2px solid ${on?C.teal:C.borderDk}`,background:on?C.teal:'transparent',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,transition:'all .15s'}),
  pill:(c=C.teal)=>({display:'inline-flex',alignItems:'center',gap:5,padding:'4px 10px',borderRadius:20,background:`${c}15`,color:c,border:`1px solid ${c}35`,fontSize:11,fontWeight:'600'}),
};

// ─── TABS ORIGINALES (flujo de orden) ────────────────────────────────────────
export const TABS = [
  {id:'paciente',    label:'1. Paciente'},
  {id:'examenes',    label:'2. Exámenes'},
  {id:'muestra',     label:'3. Muestra'},
  {id:'medico',      label:'4. Médico'},
  {id:'facturacion', label:'5. Facturación'},
];

// ─── ÍCONOS SIDEBAR ───────────────────────────────────────────────────────────
// Fábrica de íconos de línea: mismo lienzo 24×24, trazo 2 y puntas redondas
// que el resto del menú. Recibe los trazos y devuelve el componente.
const lineal = (trazos) => ({ size=16, style={} }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
    {trazos}
  </svg>
);

export const SidebarIcons = {
  comercial:     ({ size=16, style={} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
    </svg>
  ),
  nuevaOrden:    ({ size=16, style={} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/>
    </svg>
  ),
  registros:     ({ size=16, style={} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
    </svg>
  ),
  // Fase 4.1A: bandeja de preórdenes (inbox con flecha entrante)
  preordenes:    ({ size=16, style={} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/>
      <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/>
      <polyline points="9 8 12 5 15 8"/><line x1="12" y1="5" x2="12" y2="12"/>
    </svg>
  ),
  resultados:    ({ size=16, style={} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <path d="M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2v-4M9 21H5a2 2 0 0 1-2-2v-4m0 0h18"/>
    </svg>
  ),
  ingresoResultados: ({ size=16, style={} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
    </svg>
  ),
  // NUEVO: ícono para resultados aprobados (check circle)
  resultadosAprobados: ({ size=16, style={} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
      <polyline points="22 4 12 14.01 9 11.01"/>
    </svg>
  ),
  medicos:       ({ size=16, style={} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <path d="M4.8 2.3A.3.3 0 1 0 5 2H4a2 2 0 0 0-2 2v5a6 6 0 0 0 6 6v0a6 6 0 0 0 6-6V4a2 2 0 0 0-2-2h-1a.2.2 0 1 0 .3.3"/>
      <line x1="8" y1="15" x2="8" y2="22"/><line x1="16" y1="15" x2="16" y2="22"/>
      <line x1="5" y1="22" x2="19" y2="22"/>
    </svg>
  ),
  cotizaciones:  ({ size=16, style={} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/>
    </svg>
  ),
  contrasenas:   ({ size=16, style={} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
    </svg>
  ),
  caja:          ({ size=16, style={} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/>
    </svg>
  ),
  dashboard:     ({ size=16, style={} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
    </svg>
  ),
  comparativas:  ({ size=16, style={} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
    </svg>
  ),
  examenes:      ({ size=16, style={} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
    </svg>
  ),
  usuarios:      ({ size=16, style={} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  ),
  configuracion: ({ size=16, style={} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <circle cx="12" cy="12" r="3"/>
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
    </svg>
  ),
  qbOrdenes:     ({ size=16, style={} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/>
      <rect x="9" y="3" width="6" height="4" rx="2"/><line x1="9" y1="12" x2="15" y2="12"/><line x1="9" y1="16" x2="13" y2="16"/>
    </svg>
  ),
  qbResultados:  ({ size=16, style={} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <path d="M8 3H2v15h7c1.7 0 3 1.3 3 3V7c0-2.2-1.8-4-4-4z"/><path d="M16 3h6v15h-7c-1.7 0-3 1.3-3 3V7c0-2.2 1.8-4 4-4z"/>
    </svg>
  ),
  instituciones: ({ size=16, style={} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <line x1="3" y1="21" x2="21" y2="21"/>
      <path d="M5 21V7l7-4 7 4v14"/>
      <line x1="9" y1="9" x2="9" y2="9.01"/><line x1="15" y1="9" x2="15" y2="9.01"/>
      <line x1="9" y1="13" x2="9" y2="13.01"/><line x1="15" y1="13" x2="15" y2="13.01"/>
      <line x1="9" y1="17" x2="9" y2="17.01"/><line x1="15" y1="17" x2="15" y2="17.01"/>
    </svg>
  ),
  // ── Íconos de línea para los portales de clínica (24-oct-2026) ───────────
  // Reemplazan a los emojis: mismo trazo y grosor que el resto del menú.
  calendario:    lineal(<><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></>),
  agendaGeneral: lineal(<><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><line x1="8" y1="14" x2="8" y2="14.01"/><line x1="12" y1="14" x2="12" y2="14.01"/><line x1="16" y1="14" x2="16" y2="14.01"/><line x1="8" y1="18" x2="8" y2="18.01"/><line x1="12" y1="18" x2="12" y2="18.01"/></>),
  horario:       lineal(<><circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15.5 14"/></>),
  formulario:    lineal(<><rect x="8" y="2" width="8" height="4" rx="1"/><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><line x1="8" y1="11" x2="16" y2="11"/><line x1="8" y1="15" x2="13" y2="15"/></>),
  pacientes:     lineal(<><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>),
  ciclos:        lineal(<><path d="M21 12a9 9 0 1 1-3-6.7"/><polyline points="21 3 21 9 15 9"/></>),
  tubo:          lineal(<><path d="M9 3h6"/><path d="M10 3v6.5L4.6 18.4A1.7 1.7 0 0 0 6 21h12a1.7 1.7 0 0 0 1.4-2.6L14 9.5V3"/><line x1="7" y1="15" x2="17" y2="15"/></>),
  ultrasonido:   lineal(<><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/><path d="M7 12a5 5 0 0 1 10 0"/></>),
  carpeta:       lineal(<path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>),
  microscopio:   lineal(<><path d="M6 18h8"/><path d="M3 22h18"/><path d="M14 22a7 7 0 1 0 0-14h-1"/><path d="M9 14h2"/><path d="M9 12a2 2 0 0 1-2-2V6h6v4a2 2 0 0 1-2 2z"/><path d="M12 6V3a1 1 0 0 0-1-1H9a1 1 0 0 0-1 1v3"/></>),
  crio:          lineal(<><line x1="12" y1="2" x2="12" y2="22"/><line x1="4.9" y1="7" x2="19.1" y2="17"/><line x1="4.9" y1="17" x2="19.1" y2="7"/><polyline points="9 4 12 6.5 15 4"/><polyline points="9 20 12 17.5 15 20"/></>),
  grafico:       lineal(<><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></>),
  inicio:        lineal(<><path d="M3 10.5 12 3l9 7.5V20a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1z"/></>),
  cobertura:     lineal(<><path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7"/><path d="M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7"/></>),
  etiqueta:      lineal(<><path d="M20.6 13.4 13.4 20.6a2 2 0 0 1-2.8 0L2 12V2h10l8.6 8.6a2 2 0 0 1 0 2.8z"/><line x1="7" y1="7" x2="7.01" y2="7"/></>),
  bitacora:      lineal(<><path d="M3 12a9 9 0 1 0 3-6.7L3 8"/><polyline points="3 3 3 8 8 8"/><polyline points="12 7 12 12 15 14"/></>),
  farmacia:      lineal(<><path d="M10.5 20.5 3.5 13.5a5 5 0 0 1 7-7l7 7a5 5 0 0 1-7 7z"/><line x1="8.5" y1="8.5" x2="15.5" y2="15.5"/></>),
  recibo:        lineal(<><path d="M4 2v20l3-2 3 2 3-2 3 2 3-2 2 2V2l-2 2-3-2-3 2-3-2-3 2-3-2z"/><line x1="8" y1="8" x2="16" y2="8"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="8" y1="16" x2="12" y2="16"/></>),
  imagenes:      lineal(<><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></>),
  candado:       lineal(<><rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></>),
  salir:         lineal(<><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></>),
  estetoscopio:  lineal(<><path d="M5 3v6a5 5 0 0 0 10 0V3"/><path d="M10 14v2a5 5 0 0 0 10 0v-2"/><circle cx="20" cy="12" r="2"/></>),
  // Para el portal en teléfono (etapa 1): acciones y datos de una cita.
  campana:       lineal(<><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.7 21a2 2 0 0 1-3.4 0"/></>),
  telefono:      lineal(<path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.5 2.1L8 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2z"/>),
  nota:          lineal(<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="13" y2="17"/></>),
  editar:        lineal(<><path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z"/></>),
  eliminar:      lineal(<><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></>),
  mas:           lineal(<><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></>),
  anterior:      lineal(<polyline points="15 18 9 12 15 6"/>),
  siguiente:     lineal(<polyline points="9 18 15 12 9 6"/>),
  cerrar:        lineal(<><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></>),
  alerta:        lineal(<><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></>),
  check:         lineal(<><circle cx="12" cy="12" r="9"/><polyline points="8 12 11 15 16 9"/></>),
  // Etapa 2 (resultados y ultrasonidos en teléfono).
  compartir:     lineal(<><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></>),
  descargar:     lineal(<><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></>),
  buscar:        lineal(<><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></>),
  imagen:        lineal(<><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></>),
  camara:        lineal(<><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></>),
  clip:          lineal(<path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>),
  // Etapa 4 (consulta en teléfono): dictado por voz.
  microfono:     lineal(<><rect x="9" y="2" width="6" height="12" rx="3"/><path d="M5 10v1a7 7 0 0 0 14 0v-1"/><line x1="12" y1="18" x2="12" y2="22"/><line x1="8" y1="22" x2="16" y2="22"/></>),
  detener:       lineal(<rect x="6" y="6" width="12" height="12" rx="2"/>),
};

// ─── MENÚ Y ACCESO A MÓDULOS ─────────────────────────────────────────────────
// Viven en src/modulos.js (registro único): SECCIONES_MENU, MODULOS,
// puedeEntrar y moduloInicial.

// Roles de portal externo: NO son staff del laboratorio. Solo ven lo que los
// lista explícitamente. Si 'secretaria' no estuviera acá, vería todo el menú del
// laboratorio (caja, cotizaciones, catálogo, contraseñas) por el comodín '*'.
// 'socio' (2-nov-2026) tampoco es staff: solo ve sus informes financieros.
export const ROLES_PORTAL = ['medico', 'secretaria', 'manager', 'embriologa', 'socio'];

// `esStaff` = personal del laboratorio (admin/qb/op). Todo lo demás es portal
// externo: médicos y el personal de sus clínicas.
export function esStaff(role) { return !!role && !ROLES_PORTAL.includes(role); }

// ─── CORRELATIVO DE ÓRDENES ───────────────────────────────────────────────────
export function parseCorr(d) {
  if (!d) return { serie:'', num:0 };
  const m = d.match(/^([A-Z]?)(\d+)$/);
  if (!m) return { serie:'', num:0 };
  return { serie:m[1], num:parseInt(m[2],10) };
}
export function formatCorr(serie, num) { return serie + String(num).padStart(4,'0'); }
export function nextCorr(display) {
  const { serie, num } = parseCorr(display);
  if (num < 9999) return formatCorr(serie, num+1);
  const ns = serie==='' ? 'A' : String.fromCharCode(serie.charCodeAt(0)+1);
  return formatCorr(ns, 1);
}
export function isValidCorr(s) { return /^[A-Z]?\d{1,4}$/.test((s||'').trim()); }

// ─── CORRELATIVO DE CONTRASEÑAS (C-####) ─────────────────────────────────────
export const CORR_CONTRASENA_PREFIX = 'C';

export function parseCorrContrasena(d) {
  if (!d) return 0;
  const m = String(d).match(/(\d+)$/);
  return m ? parseInt(m[1], 10) : 0;
}
export function formatCorrContrasena(num) {
  return `${CORR_CONTRASENA_PREFIX}-${String(num).padStart(4,'0')}`;
}
export function nextCorrContrasena(display) {
  const num = parseCorrContrasena(display);
  const next = num >= 9999 ? 1 : num + 1;
  return formatCorrContrasena(next);
}
export function isValidCorrContrasena(s) {
  return /^(C-)?(\d{1,4})$/.test((s||'').trim().toUpperCase());
}

// ─── ESTADOS DE ORDEN ────────────────────────────────────────────────────────
export const ESTADOS_ORDEN = {
  pendiente:  { label:'Pendiente',  color:'#d97706', bg:'#fef3c7' },
  en_proceso: { label:'En proceso', color:'#4A8CF0', bg:'#DCE9FF' },
  listo:      { label:'Listo',      color:'#059669', bg:'#d1fae5' },
};

// ─── ESTADOS DE RESULTADO (flujo QB) ─────────────────────────────────────────
// CAMBIO: en_revision → control
export const ESTADOS_RESULTADO = {
  sin_resultados: { label:'Sin resultados', color:'#94a3b8', bg:'#f1f5f9' },
  borrador:       { label:'Borrador',       color:'#d97706', bg:'#fef3c7' },
  completado:     { label:'Completado',     color:'#4A8CF0', bg:'#DCE9FF' },
  control:        { label:'Controlar',      color:'#c2410c', bg:'#fff7ed' },
  aprobado:       { label:'Aprobado',       color:'#059669', bg:'#d1fae5' },
  rechazado:      { label:'Rechazado',      color:'#dc2626', bg:'#fee2e2' },
};

// ─── DATOS FIJOS ──────────────────────────────────────────────────────────────
export const HABITACIONES = [...Array.from({length:25},(_,i)=>`Habitación ${i+1}`),'Clínica de Emergencia','N/A'];
export const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
export const DL_PASS = 'InnovalabExport2026';

// ─── CATÁLOGO COMPLETO (441 exámenes) ─────────────────────────────────────────
export const CATALOG = [
  // Hematología
  { id:1,   cat:"Hematología", name:"Clasificación de Anemia", price:70 },
  { id:2,   cat:"Hematología", name:"Frote Periférico - Hematólogo", price:450 },
  { id:3,   cat:"Hematología", name:"Frote Periférico - Químico Biólogo", price:120 },
  { id:4,   cat:"Hematología", name:"Gota Gruesa", price:70 },
  { id:5,   cat:"Hematología", name:"Hematología (5 Partes)", price:75 },
  { id:6,   cat:"Hematología", name:"Hemoglobina-Hematocrito", price:70 },
  { id:7,   cat:"Hematología", name:"Inducción a Drepanocitos", price:400 },
  { id:8,   cat:"Hematología", name:"Reticulocitos", price:70 },
  { id:9,   cat:"Hematología", name:"Tiempo de Coagulación y Sangría", price:70 },
  { id:10,  cat:"Hematología", name:"Tiempo de Protrombina (TP)", price:115 },
  { id:11,  cat:"Hematología", name:"Tiempo de Tromboplastina Parcial (TTP)", price:115 },
  { id:12,  cat:"Hematología", name:"Panel de Coagulación (TP+TTP)", price:200 },
  { id:13,  cat:"Hematología", name:"Dímero D", price:265 },
  { id:14,  cat:"Hematología", name:"Fibrinógeno", price:250 },
  { id:15,  cat:"Hematología", name:"Grupo Sanguíneo y Factor Rh", price:70 },
  // Química
  { id:16,  cat:"Química", name:"Ácido Úrico", price:70 },
  { id:17,  cat:"Química", name:"Ácido Úrico en Orina 24h", price:100 },
  { id:18,  cat:"Química", name:"Alanina Aminotransferasa (ALT/GPT)", price:70 },
  { id:19,  cat:"Química", name:"Albúmina", price:70 },
  { id:20,  cat:"Química", name:"Amilasa", price:120 },
  { id:21,  cat:"Química", name:"Aspartato Aminotransferasa (AST/GOT)", price:70 },
  { id:22,  cat:"Química", name:"Bilirrubina Directa", price:70 },
  { id:23,  cat:"Química", name:"Bilirrubina Indirecta", price:70 },
  { id:24,  cat:"Química", name:"Bilirrubina Total", price:70 },
  { id:25,  cat:"Química", name:"Bilirrubinas Total/Directa/Indirecta", price:100 },
  { id:26,  cat:"Química", name:"Calcio", price:70 },
  { id:27,  cat:"Química", name:"Calcio en Orina 24h", price:100 },
  { id:28,  cat:"Química", name:"Colesterol HDL", price:70 },
  { id:29,  cat:"Química", name:"Colesterol LDL", price:70 },
  { id:30,  cat:"Química", name:"Colesterol Total", price:70 },
  { id:31,  cat:"Química", name:"Colesterol VLDL", price:70 },
  { id:32,  cat:"Química", name:"Creatinina", price:70 },
  { id:33,  cat:"Química", name:"Creatinina en Orina 24h", price:100 },
  { id:34,  cat:"Química", name:"Depuración de Creatinina", price:200 },
  { id:35,  cat:"Química", name:"Electrolitos (Na/K/Cl)", price:230 },
  { id:36,  cat:"Química", name:"Fósforo", price:70 },
  { id:37,  cat:"Química", name:"Gamma-Glutamiltransferasa (GGT)", price:90 },
  { id:38,  cat:"Química", name:"Glucosa", price:70 },
  { id:39,  cat:"Química", name:"Glucosa Postprandial (2h)", price:70 },
  { id:40,  cat:"Química", name:"Glucosa en Orina 24h", price:100 },
  { id:41,  cat:"Química", name:"Hemoglobina Glucosilada (HbA1c)", price:130 },
  { id:42,  cat:"Química", name:"Hierro Sérico", price:115 },
  { id:43,  cat:"Química", name:"Lactato Deshidrogenasa (LDH)", price:100 },
  { id:44,  cat:"Química", name:"Lipasa", price:120 },
  { id:45,  cat:"Química", name:"Magnesio", price:70 },
  { id:46,  cat:"Química", name:"Microalbuminuria", price:130 },
  { id:47,  cat:"Química", name:"Panel Diabético (Glu+HbA1c)", price:180 },
  { id:48,  cat:"Química", name:"Panel Hepático (AST+ALT+GGT+BT/BD/BI+Alb+PT)", price:350 },
  { id:49,  cat:"Química", name:"Panel Lipídico (CT+HDL+LDL+VLDL+TG)", price:250 },
  { id:50,  cat:"Química", name:"Panel Renal (BUN+Cr+ÁcÚr+EGO)", price:280 },
  { id:51,  cat:"Química", name:"Perfil Básico (Glu+BUN+Cr+CT+TG+AST+ALT)", price:370 },
  { id:52,  cat:"Química", name:"Perfil Completo (Glu+HbA1c+BUN+Cr+ÁcÚr+Panel Lip+Panel Hep+EGO)", price:700 },
  { id:53,  cat:"Química", name:"Potasio", price:90 },
  { id:54,  cat:"Química", name:"Proteínas Totales", price:70 },
  { id:55,  cat:"Química", name:"Proteínas en Orina 24h", price:100 },
  { id:56,  cat:"Química", name:"Sodio", price:90 },
  { id:57,  cat:"Química", name:"Test de O'Sullivan (Tamizaje Diabetes Gestacional)", price:100 },
  { id:58,  cat:"Química", name:"Test de Tolerancia a la Glucosa (2h)", price:130 },
  { id:59,  cat:"Química", name:"Test de Tolerancia a la Glucosa (3h)", price:160 },
  { id:60,  cat:"Química", name:"Transferrina", price:150 },
  { id:61,  cat:"Química", name:"Triglicéridos", price:70 },
  { id:62,  cat:"Química", name:"Urea (BUN)", price:70 },
  { id:63,  cat:"Química", name:"Urea en Orina 24h", price:100 },
  // Orina
  { id:64,  cat:"Orina", name:"Examen General de Orina (EGO)", price:70 },
  { id:65,  cat:"Orina", name:"Urocultivo", price:265 },
  { id:66,  cat:"Orina", name:"Urocultivo + Antibiograma", price:395 },
  { id:67,  cat:"Orina", name:"Orina de 24 horas (Volumen + Densidad)", price:70 },
  // Heces
  { id:68,  cat:"Heces", name:"Coproparasitoscópico (CPS) Seriado", price:100 },
  { id:69,  cat:"Heces", name:"Coprocultivo", price:265 },
  { id:70,  cat:"Heces", name:"Coprocultivo + Antibiograma", price:395 },
  { id:71,  cat:"Heces", name:"Examen General de Heces (EGH)", price:70 },
  { id:72,  cat:"Heces", name:"Sangre Oculta en Heces", price:100 },
  { id:73,  cat:"Heces", name:"Test de Antígeno H. Pylori en Heces", price:230 },
  // Tiroides
  { id:74,  cat:"Tiroides", name:"T3 Total", price:100 },
  { id:75,  cat:"Tiroides", name:"T4 Libre", price:100 },
  { id:76,  cat:"Tiroides", name:"T4 Total", price:100 },
  { id:77,  cat:"Tiroides", name:"TSH", price:100 },
  { id:78,  cat:"Tiroides", name:"Panel Tiroideo (TSH+T3+T4L)", price:265 },
  { id:79,  cat:"Tiroides", name:"Anti-TPO (Anticuerpos Antiperoxidasa)", price:165 },
  { id:80,  cat:"Tiroides", name:"Anti-Tiroglobulina", price:165 },
  { id:81,  cat:"Tiroides", name:"Tiroglobulina", price:200 },
  // Hormonas Reproductivas
  { id:82,  cat:"Hormonas Reproductivas", name:"DHEA-S", price:165 },
  { id:83,  cat:"Hormonas Reproductivas", name:"Estradiol (E2)", price:130 },
  { id:84,  cat:"Hormonas Reproductivas", name:"FSH", price:130 },
  { id:85,  cat:"Hormonas Reproductivas", name:"LH", price:130 },
  { id:86,  cat:"Hormonas Reproductivas", name:"Progesterona", price:130 },
  { id:87,  cat:"Hormonas Reproductivas", name:"Prolactina", price:130 },
  { id:88,  cat:"Hormonas Reproductivas", name:"Testosterona Libre", price:165 },
  { id:89,  cat:"Hormonas Reproductivas", name:"Testosterona Total", price:130 },
  { id:90,  cat:"Hormonas Reproductivas", name:"Panel Hormonal Femenino Básico (FSH+LH+E2+Prog)", price:430 },
  { id:91,  cat:"Hormonas Reproductivas", name:"Panel Hormonal Femenino Completo (FSH+LH+E2+Prog+PRL+DHEAS)", price:680 },
  { id:92,  cat:"Hormonas Reproductivas", name:"AMH - Hormona Antimülleriana", price:530 },
  // Embarazo
  { id:93,  cat:"Embarazo", name:"Beta hCG Cuantitativa", price:165 },
  { id:94,  cat:"Embarazo", name:"Beta hCG Cualitativa", price:100 },
  { id:95,  cat:"Embarazo", name:"TORCH IgG Panel Embarazo (CMV+Rubéola+Toxo+Herpes)", price:1200 },
  { id:96,  cat:"Embarazo", name:"TORCH IgM Panel Embarazo (CMV+Rubéola+Toxo+Herpes)", price:1200 },
  { id:97,  cat:"Embarazo", name:"Streptococcus Agalactiae (SGB) Cultivo Vaginal/Rectal", price:395 },
  // Oncología
  { id:98,  cat:"Oncología", name:"AFP - Alfafetoproteína", price:165 },
  { id:99,  cat:"Oncología", name:"CA 125", price:200 },
  { id:100, cat:"Oncología", name:"CA 15-3", price:200 },
  { id:101, cat:"Oncología", name:"CA 19-9", price:200 },
  { id:102, cat:"Oncología", name:"CEA - Antígeno Carcinoembrionario", price:165 },
  { id:103, cat:"Oncología", name:"PSA Libre", price:165 },
  { id:104, cat:"Oncología", name:"PSA Total", price:130 },
  { id:105, cat:"Oncología", name:"PSA Total + Libre", price:265 },
  // Hepatitis
  { id:106, cat:"Hepatitis", name:"Anti-HBc Total (Core Hepatitis B)", price:165 },
  { id:107, cat:"Hepatitis", name:"Anti-HBe (Hepatitis B)", price:165 },
  { id:108, cat:"Hepatitis", name:"Anti-HBs (Anticuerpos Hepatitis B)", price:130 },
  { id:109, cat:"Hepatitis", name:"Anti-HCV (Hepatitis C)", price:165 },
  { id:110, cat:"Hepatitis", name:"HBsAg (Antígeno Superficie Hepatitis B)", price:130 },
  { id:111, cat:"Hepatitis", name:"HBeAg (Hepatitis B)", price:165 },
  { id:112, cat:"Hepatitis", name:"Panel Hepatitis B Completo", price:500 },
  // Cultivos y Microbiología
  { id:113, cat:"Cultivos y Microbiología", name:"Antibiograma", price:150 },
  { id:114, cat:"Cultivos y Microbiología", name:"Antifungigrama", price:200 },
  { id:115, cat:"Cultivos y Microbiología", name:"Cultivo de Esputo", price:265 },
  { id:116, cat:"Cultivos y Microbiología", name:"Cultivo de Esputo + Antibiograma", price:395 },
  { id:117, cat:"Cultivos y Microbiología", name:"Cultivo de Herida", price:265 },
  { id:118, cat:"Cultivos y Microbiología", name:"Cultivo de Herida + Antibiograma", price:395 },
  { id:119, cat:"Cultivos y Microbiología", name:"Cultivo de LCR", price:395 },
  { id:120, cat:"Cultivos y Microbiología", name:"Cultivo de Secreción Ocular", price:265 },
  { id:121, cat:"Cultivos y Microbiología", name:"Cultivo de Secreción Ótica", price:265 },
  { id:122, cat:"Cultivos y Microbiología", name:"Hemocultivo (1 frasco)", price:395 },
  { id:123, cat:"Cultivos y Microbiología", name:"Hemocultivo (2 frascos)", price:680 },
  { id:124, cat:"Cultivos y Microbiología", name:"Cultivo de Hongos", price:265 },
  { id:125, cat:"Cultivos y Microbiología", name:"Cultivo Vaginal/Cervical", price:265 },
  { id:126, cat:"Cultivos y Microbiología", name:"Cultivo Vaginal + Antibiograma", price:395 },
  // Inmunología
  { id:127, cat:"Inmunología", name:"Anticardiolipina IgG", price:325 },
  { id:128, cat:"Inmunología", name:"Anticardiolipina IgM", price:325 },
  { id:129, cat:"Inmunología", name:"Anticardiolipina IgA", price:325 },
  { id:130, cat:"Inmunología", name:"Panel Anticardiolipina IgG/IgM", price:595 },
  { id:131, cat:"Inmunología", name:"Anticoagulante Lúpico (Drvvt)", price:525 },
  { id:132, cat:"Inmunología", name:"Anti-Beta2 Glicoproteína I IgG", price:325 },
  { id:133, cat:"Inmunología", name:"Anti-Beta2 Glicoproteína I IgM", price:325 },
  { id:134, cat:"Inmunología", name:"Anti-CCP (Anticuerpos Antipéptido Cíclico Citrulinado)", price:325 },
  { id:135, cat:"Inmunología", name:"Anti-dsDNA (Anticuerpos Anti-DNA Doble Cadena)", price:265 },
  { id:136, cat:"Inmunología", name:"Anti-Smith", price:265 },
  { id:137, cat:"Inmunología", name:"C3 Complemento", price:165 },
  { id:138, cat:"Inmunología", name:"C4 Complemento", price:165 },
  { id:139, cat:"Inmunología", name:"Panel ANCA (C-ANCA + P-ANCA)", price:730 },
  // Diabetes Avanzada
  { id:140, cat:"Diabetes Avanzada", name:"Insulina Basal", price:165 },
  { id:141, cat:"Diabetes Avanzada", name:"Péptido C", price:200 },
  { id:142, cat:"Diabetes Avanzada", name:"Índice HOMA-IR (Glu+Insulina)", price:265 },
  // Vitaminas y Minerales
  { id:143, cat:"Vitaminas y Minerales", name:"25-OH Vitamina D", price:265 },
  { id:144, cat:"Vitaminas y Minerales", name:"Vitamina B12", price:165 },
  { id:145, cat:"Vitaminas y Minerales", name:"Folato (Ácido Fólico)", price:165 },
  { id:146, cat:"Vitaminas y Minerales", name:"Ferritina", price:130 },
  { id:147, cat:"Vitaminas y Minerales", name:"Hierro + TIBC (Capacidad Total de Fijación)", price:200 },
  { id:148, cat:"Vitaminas y Minerales", name:"Zinc", price:165 },
  { id:149, cat:"Vitaminas y Minerales", name:"Cobre", price:165 },
  { id:150, cat:"Vitaminas y Minerales", name:"Selenio", price:265 },
  // Drogas y Toxicología
  { id:151, cat:"Drogas y Toxicología", name:"Panel de Drogas en Orina (5 drogas)", price:265 },
  { id:152, cat:"Drogas y Toxicología", name:"Panel de Drogas en Orina (10 drogas)", price:395 },
  { id:153, cat:"Drogas y Toxicología", name:"Alcohol en Sangre (Etanol)", price:200 },
  { id:154, cat:"Drogas y Toxicología", name:"Plomo en Sangre", price:395 },
  { id:155, cat:"Drogas y Toxicología", name:"Mercurio en Sangre", price:395 },
  // Cardiología
  { id:156, cat:"Cardiología", name:"Troponina I", price:265 },
  { id:157, cat:"Cardiología", name:"CK-MB (Creatina Kinasa MB)", price:130 },
  { id:158, cat:"Cardiología", name:"CK Total", price:100 },
  { id:159, cat:"Cardiología", name:"BNP (Péptido Natriurético)", price:530 },
  { id:160, cat:"Cardiología", name:"Proteína C Reactiva Ultrasensible (hsCRP)", price:165 },
  { id:161, cat:"Cardiología", name:"Homocisteína", price:265 },
  { id:162, cat:"Cardiología", name:"Lipoproteína (a)", price:265 },
  { id:163, cat:"Cardiología", name:"Apolipoproteína A1", price:200 },
  { id:164, cat:"Cardiología", name:"Apolipoproteína B", price:200 },
  // Glándulas Suprarrenales
  { id:165, cat:"Glándulas Suprarrenales", name:"Cortisol Basal", price:165 },
  { id:166, cat:"Glándulas Suprarrenales", name:"Cortisol Post-ACTH", price:265 },
  { id:167, cat:"Glándulas Suprarrenales", name:"ACTH", price:265 },
  { id:168, cat:"Glándulas Suprarrenales", name:"Aldosterona", price:265 },
  { id:169, cat:"Glándulas Suprarrenales", name:"Renina", price:265 },
  // Infecciosas
  { id:170, cat:"Infecciosas", name:"Antígeno de Helicobacter Pylori", price:165 },
  { id:171, cat:"Infecciosas", name:"Antígeno de Streptococcus Grupo A", price:165 },
  { id:172, cat:"Infecciosas", name:"Antígeno Cryptococcus", price:265 },
  { id:173, cat:"Infecciosas", name:"Bordetella Pertussis IgG", price:325 },
  { id:174, cat:"Infecciosas", name:"Bordetella Pertussis IgM", price:325 },
  { id:175, cat:"Infecciosas", name:"Chagas IgG", price:165 },
  { id:176, cat:"Infecciosas", name:"Chagas IgM", price:165 },
  { id:177, cat:"Infecciosas", name:"Chlamydia Trachomatis IgG", price:325 },
  { id:178, cat:"Infecciosas", name:"Chlamydia Trachomatis IgM", price:325 },
  { id:179, cat:"Infecciosas", name:"Citomegalovirus CMV IgG", price:165 },
  { id:180, cat:"Infecciosas", name:"Citomegalovirus CMV IgM", price:165 },
  { id:181, cat:"Infecciosas", name:"Clamidia Neumoniae IgG", price:325 },
  { id:182, cat:"Infecciosas", name:"Clamidia Neumoniae IgM", price:325 },
  { id:183, cat:"Infecciosas", name:"Dengue IgG", price:165 },
  { id:184, cat:"Infecciosas", name:"Dengue IgM", price:165 },
  { id:185, cat:"Infecciosas", name:"Dengue NS1 (Antígeno)", price:265 },
  { id:186, cat:"Infecciosas", name:"Epstein-Barr (VEB) IgG VCA", price:265 },
  { id:187, cat:"Infecciosas", name:"Epstein-Barr (VEB) IgM VCA", price:265 },
  { id:188, cat:"Infecciosas", name:"Herpes Simple 1 IgG", price:165 },
  { id:189, cat:"Infecciosas", name:"Herpes Simple 1 IgM", price:165 },
  { id:190, cat:"Infecciosas", name:"Herpes Simple 2 IgG", price:165 },
  { id:191, cat:"Infecciosas", name:"Herpes Simple 2 IgM", price:165 },
  { id:192, cat:"Infecciosas", name:"Herpes Zóster IgG", price:350 },
  { id:193, cat:"Infecciosas", name:"Herpes Zóster IgM", price:350 },
  { id:194, cat:"Infecciosas", name:"HIV Anticuerpos 1 y 2", price:130 },
  { id:195, cat:"Infecciosas", name:"HIV Antígeno P24", price:230 },
  { id:196, cat:"Infecciosas", name:"Huddleson Brucella Abortus", price:315 },
  { id:197, cat:"Infecciosas", name:"Leishmaniasis Exudado de Lesión", price:180 },
  { id:198, cat:"Infecciosas", name:"Leptospira Campo Oscuro", price:325 },
  { id:199, cat:"Infecciosas", name:"Leptospira IgG", price:180 },
  { id:200, cat:"Infecciosas", name:"Leptospira IgM", price:180 },
  { id:201, cat:"Infecciosas", name:"Mycoplasma Pneumoniae IgG", price:325 },
  { id:202, cat:"Infecciosas", name:"Mycoplasma Pneumoniae IgM", price:325 },
  { id:203, cat:"Infecciosas", name:"Paludismo/Malaria (Plasmodium) Antígeno", price:270 },
  { id:204, cat:"Infecciosas", name:"Paperas IgG", price:350 },
  { id:205, cat:"Infecciosas", name:"Paperas IgM", price:350 },
  { id:206, cat:"Infecciosas", name:"Parvovirus B19 IgM", price:395 },
  { id:207, cat:"Infecciosas", name:"Rubéola IgG", price:100 },
  { id:208, cat:"Infecciosas", name:"Rubéola IgM", price:100 },
  { id:209, cat:"Infecciosas", name:"Saccharomyces Cerevisiae (Anticuerpos Totales)", price:365 },
  { id:210, cat:"Infecciosas", name:"Salmonella Typhi Ac. IgG", price:135 },
  { id:211, cat:"Infecciosas", name:"Salmonella Typhi Ac. IgM", price:135 },
  { id:212, cat:"Infecciosas", name:"Sarampión IgG", price:350 },
  { id:213, cat:"Infecciosas", name:"Sarampión IgM", price:350 },
  { id:214, cat:"Infecciosas", name:"Diagnóstico de Sarna", price:110 },
  { id:215, cat:"Infecciosas", name:"Strep Test", price:180 },
  { id:216, cat:"Infecciosas", name:"TORCH IgG", price:395 },
  { id:217, cat:"Infecciosas", name:"TORCH IgM", price:395 },
  { id:218, cat:"Infecciosas", name:"Toxocara Canis IgG", price:595 },
  { id:219, cat:"Infecciosas", name:"Toxocara Canis IgM", price:595 },
  { id:220, cat:"Infecciosas", name:"Toxoplasma Avidez", price:2700 },
  { id:221, cat:"Infecciosas", name:"Toxoplasma IgG", price:100 },
  { id:222, cat:"Infecciosas", name:"Toxoplasma IgM", price:100 },
  { id:223, cat:"Infecciosas", name:"FTA-ABS IgG (Anticuerpos Treponémicos)", price:295 },
  { id:224, cat:"Infecciosas", name:"FTA-ABS IgM (Anticuerpos Treponémicos)", price:295 },
  { id:225, cat:"Infecciosas", name:"FTA-ABS Totales (Anticuerpos Treponémicos)", price:295 },
  { id:226, cat:"Infecciosas", name:"Treponema Pallidum IgM", price:165 },
  { id:227, cat:"Infecciosas", name:"Tuberculosis Anticuerpos Totales", price:290 },
  { id:228, cat:"Infecciosas", name:"Varicela IgG", price:350 },
  { id:229, cat:"Infecciosas", name:"Varicela IgM", price:350 },
  { id:230, cat:"Infecciosas", name:"VDRL Sífilis Preliminar", price:100 },
  { id:231, cat:"Infecciosas", name:"Virus Influenzae A y B", price:345 },
  { id:232, cat:"Infecciosas", name:"Virus Sincitial Respiratorio en Moco", price:345 },
  { id:233, cat:"Infecciosas", name:"Western Blot HIV", price:2300 },
  { id:234, cat:"Infecciosas", name:"Widal", price:200 },
  { id:235, cat:"Infecciosas", name:"Zika IgM", price:270 },
  // Serología
  { id:236, cat:"Serología", name:"ANA - Anticuerpos Antinucleares (Látex)", price:120 },
  { id:237, cat:"Serología", name:"ASO / ASL - Antiestreptolisina O", price:110 },
  { id:238, cat:"Serología", name:"Factor Reumatoide (FR)", price:110 },
  { id:239, cat:"Serología", name:"PCR - Proteína C Reactiva", price:110 },
  { id:240, cat:"Serología", name:"VDRL Sífilis Preliminar (Serología)", price:115 },
  { id:241, cat:"Serología", name:"Widal (Serología)", price:115 },
  // Inmunología Gastrointestinal
  { id:242, cat:"Inmunología Gastrointestinal", name:"Anti Endomisiales por Inmunoflurescencia IgA", price:385 },
  { id:243, cat:"Inmunología Gastrointestinal", name:"Anti Endomisiales por Inmunoflurescencia IgG", price:385 },
  { id:244, cat:"Inmunología Gastrointestinal", name:"Gliadina Deaminada IgA (Anti-DGP)", price:160 },
  { id:245, cat:"Inmunología Gastrointestinal", name:"Gliadinas IgG (Anti-DGP)", price:160 },
  { id:246, cat:"Inmunología Gastrointestinal", name:"Panel de Gliadinas IgG/IgA", price:320 },
  { id:247, cat:"Inmunología Gastrointestinal", name:"Panel Transglutaminasa IgG/IgA", price:320 },
  { id:248, cat:"Inmunología Gastrointestinal", name:"Transglutaminasa IgA (Endomisiales IgA)", price:160 },
  { id:249, cat:"Inmunología Gastrointestinal", name:"Transglutaminasa IgG (Endomisiales IgG)", price:160 },
  // Inmunoglobulinas
  { id:250, cat:"Inmunoglobulinas", name:"IgA - Inmunoglobulina A", price:115 },
  { id:251, cat:"Inmunoglobulinas", name:"IgE - Inmunoglobulina E", price:130 },
  { id:252, cat:"Inmunoglobulinas", name:"IgG - Inmunoglobulina G", price:115 },
  { id:253, cat:"Inmunoglobulinas", name:"IgM - Inmunoglobulina M", price:115 },
  { id:254, cat:"Inmunoglobulinas", name:"Subtipos de IgG", price:2500 },
  // Microscopía
  { id:255, cat:"Microscopía", name:"Líquido Pleural (Gram, Glucosa, Proteínas, DHL, Fórmula)", price:400 },
  { id:256, cat:"Microscopía", name:"BK Esputo - Ziehl Neelsen", price:65 },
  { id:257, cat:"Microscopía", name:"Detección de Alergias por Ácaros", price:200 },
  { id:258, cat:"Microscopía", name:"BK Muestras Varias", price:65 },
  { id:259, cat:"Microscopía", name:"BK Orina - Ziehl Neelsen", price:65 },
  { id:260, cat:"Microscopía", name:"Eosinófilos en Moco", price:85 },
  { id:261, cat:"Microscopía", name:"Espermograma", price:265 },
  { id:262, cat:"Microscopía", name:"Giemsa", price:85 },
  { id:263, cat:"Microscopía", name:"Gram", price:65 },
  { id:264, cat:"Microscopía", name:"Investigación de Hemoparásitos", price:135 },
  { id:265, cat:"Microscopía", name:"Kinyoun", price:85 },
  { id:266, cat:"Microscopía", name:"KOH", price:65 },
  { id:267, cat:"Microscopía", name:"Recuento de Espermatozoides", price:165 },
  { id:268, cat:"Microscopía", name:"Secreciones en Fresco (Solución Salina)", price:65 },
  { id:269, cat:"Microscopía", name:"Tinta China", price:95 },
  { id:270, cat:"Microscopía", name:"Tzank", price:250 },
  // Patología
  { id:271, cat:"Patología", name:"Papanicolaou Muestras Varias", price:535 },
  { id:272, cat:"Patología", name:"Papanicolaou Vaginal", price:100 },
  // Biología Molecular
  { id:273, cat:"Biología Molecular", name:"VIH-1 Carga Viral", price:1350 },
  { id:274, cat:"Biología Molecular", name:"Hepatitis B Carga Viral", price:1350 },
  { id:275, cat:"Biología Molecular", name:"Hepatitis C Carga Viral", price:1350 },
  { id:276, cat:"Biología Molecular", name:"Clostridium Difficile por PCR", price:1050 },
  { id:277, cat:"Biología Molecular", name:"Influenza y Virus Sincitial Respiratorio por PCR", price:1350 },
  { id:278, cat:"Biología Molecular", name:"VPH - Papiloma Virus Humano", price:885 },
  { id:279, cat:"Biología Molecular", name:"VPH - Kit de Toma de Muestra", price:97.5 },
  { id:280, cat:"Biología Molecular", name:"Chlamydia Trachomatis y Neisseria Gonorrhoeae (Molecular)", price:900 },
  { id:281, cat:"Biología Molecular", name:"GeneXpert Tuberculosis", price:1735 },
  { id:282, cat:"Biología Molecular", name:"Mycobacterium Tuberculosis y Resistencia a Rifampicina (PCR)", price:885 },
  { id:283, cat:"Biología Molecular", name:"Panel de Sepsis (24 microorganismos)", price:4890 },
  { id:284, cat:"Biología Molecular", name:"Panel Gastrointestinal (22 microorganismos)", price:4170 },
  { id:285, cat:"Biología Molecular", name:"Panel de Meningitis/Encefalitis (14 microorganismos)", price:5200 },
  { id:286, cat:"Biología Molecular", name:"Panel Respiratorio (20 virus)", price:4170 },
  { id:287, cat:"Biología Molecular", name:"Paternidad ADN", price:5700 },
  { id:288, cat:"Biología Molecular", name:"Kit Toma de Muestra de Paternidad", price:105 },
  { id:289, cat:"Biología Molecular", name:"Zika por PCR", price:750 },
  // Paneles Alergia
  { id:290, cat:"Paneles Alergia", name:"Panel Alergia Alimentos IgE", price:1430 },
  { id:291, cat:"Paneles Alergia", name:"Panel Alergia Inhalantes IgE", price:1430 },
  { id:292, cat:"Paneles Alergia", name:"Panel Alergia Pediátrica IgE", price:2025 },
];


// Título de la ventana de cotización = nombre sugerido del PDF al "Guardar como PDF".
// "Cotización Innovalab - Nombre Paciente"; sin paciente queda el título genérico.
// Quita caracteres inválidos en nombres de archivo y escapa HTML.
export const tituloCotizacion = (nombre) => {
  const limpio = String(nombre || '').replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim();
  const t = limpio ? `Cotización Innovalab - ${limpio}` : 'Cotización Innovalab';
  return t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
};
